import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import PhoneField, { DIAL_CODES } from "../components/PhoneField";
import PasswordField from "../components/PasswordField";
import { login } from "../lib/api";
import { saveAuth } from "../lib/auth";

export default function Login() {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState(DIAL_CODES[0]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password) {
      toast.error("Please enter phone and password");
      return;
    }
    setLoading(true);
    try {
      const fullPhone = `${dial.code}${phone.trim()}`;
      const res = await login({ phone: fullPhone, password });
      if (res.needs_verification) {
        toast.message("Account not verified", { description: `OTP sent to ${fullPhone}` });
        nav("/verify-otp", { state: { phone: res.phone, purpose: "signup" } });
        return;
      }
      saveAuth(res.token, res.user);
      toast.success(`Welcome back, ${res.user.name}`);
      nav("/payment", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-white flex flex-col">
        <div className="screen flex-1 flex flex-col">
          <div className="pt-10 flex justify-center">
            <Logo size="lg" />
          </div>

          <div className="mt-14 text-center">
            <h1
              className="font-display font-bold text-[28px] tracking-tight text-black"
              data-testid="login-title"
            >
              Sign in
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <Field label="Mobile number">
              <PhoneField
                phone={phone}
                onPhoneChange={setPhone}
                dial={dial}
                onDialChange={setDial}
                testIdPhone="login-phone-input"
                testIdDial="login-dial-select"
              />
            </Field>

            <Field label="Password">
              <PasswordField
                testId="login-password-input"
                value={password}
                onChange={setPassword}
                placeholder="Enter password"
                autoComplete="current-password"
              />
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-[12.5px] font-medium text-brand hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </Field>

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="btn-brand mx-auto h-11 flex items-center justify-center text-[14px] min-w-[220px]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
            </button>
          </form>

          <div className="divider-or">or</div>

          <Link to="/signup" data-testid="go-to-signup-link" className="flex justify-center">
            <button className="mx-auto h-11 px-6 rounded-full border border-neutral-200 bg-white text-black font-medium hover:bg-neutral-50 transition-colors text-[13.5px] min-w-[220px]">
              Create a new account
            </button>
          </Link>

          <div className="mt-auto pt-10 pb-2 text-center text-[11px] text-neutral-400 leading-relaxed">
            By continuing you agree to our{" "}
            <span className="text-neutral-600 underline">Terms</span> &{" "}
            <span className="text-neutral-600 underline">Privacy Policy</span>.
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
